/**
 * First-run onboarding (web.md §4 "Additions"): on an EMPTY workspace that has not been onboarded, a
 * welcome dialog offers "Add a source" (→ the new-source editor) or "Load sample data" (the prototype's
 * sample workspace in the four folders), and "Sign in" for someone whose workspace already lives in an
 * account. Any choice — including "Not now" — marks `local_settings.onboarded`, so it is shown once per
 * device database.
 *
 * A device signed in to an account with sync never sees it: its workspace comes from the account (a
 * provisioned account's first pull fills it), and offering sample data there would push samples into it.
 *
 * Mounted once at the app root. It only opens on a reader route, and only when the workspace is still
 * empty after a short settling delay read straight from the engine (a page that fills the workspace
 * right after start — a restore, a test harness — never sees it).
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Engine } from '@digester/core';
import { DialogFrame, isDialogRegistered, openDialog, registerDialog, type DialogProps } from '../app/dialogs.js';
import { reportError } from '../app/errors.js';
import { showToast } from '../app/Toast.js';
import { useEngine } from '../engine-client/EngineProvider.js';
import { Icon } from '../icons/Icon.js';
import { loadSampleData } from './sample.js';
import './shared.js';

const SETTLE_MS = 1200;

const READER_ROUTE = /^\/(f|v|corrections)(\/|$)/;

async function isEmptyWorkspace(engine: Engine): Promise<boolean> {
  const [local, sources, counts] = await Promise.all([engine.settings.getLocal(), engine.sources.list({ includeArchived: true }), engine.messages.counts()]);
  if (local.onboarded) return false;
  if (local.account && (local.entitlement?.limits.syncDevices ?? 0) > 0) return false;
  if (sources.length > 0) return false;
  return Object.values(counts.folders).every((c) => c.total === 0);
}

export function OnboardingDialog({ close, dialogId, onAddSource, onSignIn }: DialogProps & { onAddSource(): void; onSignIn(): void }) {
  const engine = useEngine();
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const finish = async (): Promise<void> => {
    try {
      await engine.settings.setLocal('onboarded', true);
    } catch (error) {
      reportError(error);
    }
  };

  const loadSample = async (): Promise<void> => {
    setBusy(true);
    try {
      const report = await loadSampleData(engine);
      await finish();
      close();
      navigate('/f/priority');
      showToast(report.skipped.length ? `Sample data loaded · ${report.skipped.length} items need Pro` : 'Sample data loaded');
    } catch (error) {
      reportError(error);
      setBusy(false);
    }
  };

  return (
    <DialogFrame
      title="Welcome to Digester"
      className="small-dialog onboarding-dialog"
      dialogId={dialogId}
      onClose={() => {
        void finish();
        close();
      }}
    >
      <p>Digester gathers the sources you choose, sorts new messages into folders and keeps what matters to you.</p>
      <div className="onboarding-options">
        <button
          type="button"
          className="onboarding-option"
          disabled={busy}
          onClick={() => {
            void finish();
            close();
            onAddSource();
          }}
        >
          <span className="onboarding-option-icon">
            <Icon name="rss" />
          </span>
          <span>
            <strong>Add a source</strong>
            <small>A feed, a website, a newsletter, a Telegram channel or a YouTube channel.</small>
          </span>
          <Icon name="chevron-right" />
        </button>
        <button type="button" className="onboarding-option" disabled={busy} onClick={() => void loadSample()}>
          <span className="onboarding-option-icon">
            <Icon name={busy ? 'loader' : 'sparkles'} />
          </span>
          <span>
            <strong>{busy ? 'Loading sample data…' : 'Load sample data'}</strong>
            <small>Sample messages in Inbox, Priority, Weak and Dismissed, with topics and filters to explore.</small>
          </span>
          <Icon name="chevron-right" />
        </button>
      </div>
      <p className="field-help">Digester collects new messages while one of its tabs is open; nothing is collected while every tab is closed.</p>
      <div className="dialog-footer">
        <button
          type="button"
          className="secondary-button"
          disabled={busy}
          onClick={() => {
            void finish();
            close();
            onSignIn();
          }}
        >
          I have an account — Sign in
        </button>
        <button
          type="button"
          className="secondary-button"
          disabled={busy}
          onClick={() => {
            void finish();
            close();
          }}
        >
          Not now
        </button>
      </div>
    </DialogFrame>
  );
}

export function registerOnboardingDialog(): void {
  if (!isDialogRegistered('onboarding')) registerDialog<{ onAddSource(): void; onSignIn(): void }>('onboarding', OnboardingDialog);
}

export function Onboarding() {
  const engine = useEngine();
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const onReader = READER_ROUTE.test(routerLocation.pathname);
  const shown = useRef(false);
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!onReader || shown.current) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      void (async () => {
        try {
          if (cancelled || shown.current || !(await isEmptyWorkspace(engine)) || cancelled) return;
          shown.current = true;
          registerOnboardingDialog();
          openDialog('onboarding', {
            onAddSource: () => navigateRef.current('/settings/sources/new'),
            onSignIn: () => navigateRef.current('/signin'),
          });
        } catch {
          // The workspace could not be read (the engine is still starting or failed): no onboarding now.
        }
      })();
    }, SETTLE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [engine, onReader]);

  return null;
}
